import { useState } from 'react';
import { addNote } from '../../lib/api';
import { useApiMutation } from '../../hooks/useApi';
import type { Customer } from '../../types';
import Modal from '../Modal';

interface AddNotesProps {
  customer: Customer;
  onClose: () => void;
  onSuccess: () => void;
}

const noteCategories = [
  { value: 'general', label: 'General' },
  { value: 'support', label: 'Support' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'billing', label: 'Billing' },
  { value: 'verification', label: 'Verification' },
];

export default function AddNotes({ customer, onClose, onSuccess }: AddNotesProps) {
  const [note, setNote] = useState('');
  const [category, setCategory] = useState('general');

  const { loading, error, execute } = useApiMutation(addNote, {
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await execute({
      customerId: customer.id,
      customerEmail: customer.email,
      note,
      category,
    });
  };

  return (
    <Modal title="Add Note" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-sm text-gray-700">
              Adding note for{' '}
              <strong>
                {customer.first_name} {customer.last_name}
              </strong>{' '}
              ({customer.email})
            </p>
          </div>

          <div>
            <label className="label">Category</label>
            <select
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {noteCategories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Note</label>
            <textarea
              className="input"
              rows={5}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter your note..."
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={loading || !note.trim()}>
            {loading ? 'Adding...' : 'Add Note'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
